import type { QuarterlyPeriodPoint } from '@app-types/performanceTrends';

export const QUARTERLY_MIN_PERIOD_PITCH = 24;
export const QUARTERLY_GRID_RIGHT = 8;

export interface QuarterlyViewport {
  start: number;
  end: number;
}

export interface QuarterlyViewportAnchor {
  key: string;
  index: number;
  atEnd: boolean;
}

export const quarterlyVisibleCount = (
  width: number,
  gridLeft: number,
  pointCount: number,
): number => Math.min(pointCount, Math.max(
  1,
  Math.floor((width - gridLeft - QUARTERLY_GRID_RIGHT) / QUARTERLY_MIN_PERIOD_PITCH),
));

export const resolveQuarterlyViewport = (
  points: QuarterlyPeriodPoint[],
  visibleCount: number,
  anchor: QuarterlyViewportAnchor | null,
): QuarterlyViewport => {
  const count = Math.min(points.length, Math.max(1, visibleCount));
  const maxStart = Math.max(0, points.length - count);
  const keyIndex = anchor ? points.findIndex((point) => point.key === anchor.key) : -1;
  const start = !anchor || anchor.atEnd
    ? maxStart
    : Math.min(maxStart, Math.max(0, keyIndex >= 0 ? keyIndex : anchor.index));
  return { start, end: Math.max(0, start + count - 1) };
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  value !== null && typeof value === 'object'
);
const finite = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value)
);

/** ECharts emits percentages for slider drags and can batch zoom actions. */
export const quarterlyViewportFromEvent = (
  event: unknown,
  points: QuarterlyPeriodPoint[],
  visibleCount: number,
): QuarterlyViewport | null => {
  if (!isRecord(event) || points.length === 0) return null;
  const payload = Array.isArray(event.batch) ? event.batch[0] : event;
  if (!isRecord(payload)) return null;
  const lastIndex = points.length - 1;
  const indexOfValue = (value: unknown): number | null => {
    if (finite(value)) return value;
    if (typeof value === 'string') {
      const index = points.findIndex((point) => point.key === value);
      return index >= 0 ? index : null;
    }
    return null;
  };
  const explicitStart = indexOfValue(payload.startValue);
  const explicitEnd = indexOfValue(payload.endValue);
  const start = explicitStart ?? (finite(payload.start)
    ? payload.start * lastIndex / 100
    : explicitEnd !== null ? explicitEnd - visibleCount + 1 : null);
  if (start === null) return null;
  const maxStart = Math.max(0, points.length - Math.max(1, visibleCount));
  const clamped = Math.min(maxStart, Math.max(0, Math.round(start)));
  return { start: clamped, end: Math.min(lastIndex, clamped + visibleCount - 1) };
};

export const anchorQuarterlyViewport = (
  points: QuarterlyPeriodPoint[],
  viewport: QuarterlyViewport,
): QuarterlyViewportAnchor | null => points[viewport.start] ? {
  key: points[viewport.start].key,
  index: viewport.start,
  atEnd: viewport.end >= points.length - 1,
} : null;
